import Tippy from '@tippyjs/react';
import classNames from 'classnames';
import * as React from 'react';

interface TabPanelProps {
  title: string;
  children: React.ReactNode;
  className?: string;
  datatour?: string;
  disabled?: boolean;
  disabledMessage?: string;
}

const { useState, useEffect, useCallback, Fragment } = React;

export const Panel = ({ children }: TabPanelProps): JSX.Element => {
  return <>{children}</>;
};

export interface TabsProps {
  children: React.ReactNode | any;
  selected?: number;
  size: 'small' | 'large';
  className?: string;
  tabClassName?: string;
  workspaceId?: string;
  extra?: JSX.Element;
  onChange?: (index: number) => void;
}

export const Tabs = ({
  children,
  workspaceId,
  selected: defaultSelected = 0,
  size,
  className,
  extra,
  tabClassName,
  onChange,
}: TabsProps): JSX.Element => {
  const [selected, setSelected] = useState<number>(defaultSelected);
  useEffect(() => {
    setSelected(defaultSelected);
  }, [defaultSelected]);
  const handleChange = useCallback((index: number): void => {
    setSelected(index);
    onChange?.(index);
  }, []);

  useEffect(() => {
    const selectedTab = children?.[selected];
    if (selectedTab?.props?.disabled) {
      const index = children?.findIndex((child) => !child?.props?.disabled);
      handleChange(index);
    }
  }, [children, selected]);

  return (
    <Fragment>
      <div className={classNames({ flex: true, [className || '']: !!className })}>
        {workspaceId ? (
          <div className="text-gray-500 pb-4">Public to all workspace members</div>
        ) : (
          <ul className="inline-flex">
            {React.Children.map(children, (child, index) => {
              const disabled = child?.props.disabled;
              const classNameLi = classNames(
                {
                  'font-medium cursor-pointer mr-26 px-2 pb-10': true,
                  'text-primary-500 border-primary-500 border-b-2': index === selected,
                  'text-sm': size === 'small',
                  'text-gray-300': disabled,
                  'text-gray-500': !disabled && index !== selected,
                },
                tabClassName
              );
              return (
                <TabButton
                  disabled={disabled}
                  disabledMessage={child?.props.disabledMessage}
                  index={index}
                  className={classNameLi}
                  key={index}
                  onClick={handleChange}
                >
                  {child?.props.title}
                </TabButton>
              );
            })}
          </ul>
        )}
        {extra}
      </div>
      {children ? children?.[selected] || children : null}
    </Fragment>
  );
};

const TabButton = ({
  className,
  onClick,
  index,
  children,
  disabled,
  disabledMessage,
}: {
  className: string;
  onClick: (index: number) => void;
  index: number;
  children: React.ReactNode;
  disabled?: boolean;
  disabledMessage?: string;
}) => {
  function handleOnClick() {
    onClick(index);
  }
  const tourText = React.Children?.map(children, (child) => child);
  return (
    <Tippy content={disabledMessage} disabled={!disabled || !disabledMessage}>
      <button
        className={className}
        onClick={disabled ? undefined : handleOnClick}
        data-tour={tourText}
      >
        {children}
      </button>
    </Tippy>
  );
};
